import { useState } from 'react';
import {
  Box, Button, Menu, MenuItem, ListItemText, ListItemIcon,
  Typography, Divider, Chip,
} from '@mui/material';
import {
  School as SchoolIcon,
  Check as CheckIcon,
  UnfoldMore as SwitchIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

// Multi-school Phase 3a — the active-school picker.
//
// Renders nothing at all unless the caller is a super-user on a
// deployment with the multi-school feature enabled, so it is
// invisible on every single-school installation.
//
// Picking a school stores the choice and reloads the page: each screen
// keeps its own fetched rows in component state, and a hard reload is
// the only way to be certain none of the previous school's figures
// stay on screen. See AuthContext.switchSchool.
export default function SchoolSwitcher() {
  const {
    currentSchool, switchableSchools, canSwitchSchools,
    multiSchoolEnabled, switchSchool,
  } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);

  if (!multiSchoolEnabled || !canSwitchSchools) return null;
  if (!switchableSchools || switchableSchools.length < 2) return null;

  const activeId = currentSchool?.id;
  const activeName = currentSchool?.name || 'Select school';

  const pick = (id) => {
    setAnchorEl(null);
    if (String(id) !== String(activeId)) switchSchool(id);
  };

  return (
    <Box>
      <Button
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        startIcon={<SchoolIcon sx={{ fontSize: 18 }} />}
        endIcon={<SwitchIcon sx={{ fontSize: 16 }} />}
        sx={{
          textTransform: 'none', fontWeight: 600, fontSize: 12.5,
          color: 'text.primary', bgcolor: 'action.hover',
          border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
          px: 1.25, maxWidth: 230,
          '&:hover': { bgcolor: 'action.selected' },
        }}
      >
        <Box component="span" sx={{
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {activeName}
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { minWidth: 260, borderRadius: 2, mt: 0.5 } }}
      >
        <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.disabled' }}>
            SWITCH SCHOOL
          </Typography>
        </Box>
        <Divider />
        {switchableSchools.map((s) => {
          const isActive = String(s.id) === String(activeId);
          return (
            <MenuItem
              key={s.id}
              selected={isActive}
              onClick={() => pick(s.id)}
              sx={{ py: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {isActive ? <CheckIcon fontSize="small" color="primary" /> : null}
              </ListItemIcon>
              <ListItemText
                primary={s.name}
                primaryTypographyProps={{ fontSize: 13.5, fontWeight: isActive ? 700 : 500 }}
              />
              <Chip
                label={s.code}
                size="small"
                sx={{ ml: 1, height: 20, fontSize: 10.5, fontWeight: 600 }}
              />
            </MenuItem>
          );
        })}
        <Divider />
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Switching reloads the page so no figures from the previous
            school stay on screen.
          </Typography>
        </Box>
      </Menu>
    </Box>
  );
}
